<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon
        class="mt-n1"
        v-on="on"
      >
        <v-icon color="purple">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Backup Approver"
      text="Update Backup Approver"
      class="mb-0"
    > 
      <v-container v-if="dialog && !loading">
        <v-row no-gutters>
          <v-col
            cols="12"
            class="py-0"
          >
            <v-autocomplete
              v-model="localBackupApprover.delegator_uid"
              v-validate="'required'"
              :error-messages="errors.collect('delegator_uid')"
              data-vv-name="delegator_uid"  
              data-vv-as="delegator"  
              :items="employees"
              label="Delegator"
              item-text="person.name"
              item-value="employee_uid"
              @change="getBackup(localBackupApprover.delegator_uid)"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <v-autocomplete
              v-model="localBackupApprover.employee_uid"
              v-validate="'required'"
              :error-messages="errors.collect('employee_uid')"
              data-vv-name="employee_uid"  
              data-vv-as="backup"  
              :items="backups"
              :disabled="!localBackupApprover.delegator_uid"
              :loading="loading"
              label="Backup"
              item-text="person.name"
              item-value="employee_uid"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="localBackupApprover.start_date"
                  v-validate="'required'"
                  label="Start Date"
                  :error-messages="errors.collect('start_date')"
                  data-vv-name="start_date"  
                  data-vv-as="start date"    
                  readonly
                  class="label-position"
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localBackupApprover.start_date"
                no-title
                :min="today"
                :max="localBackupApprover.end_date"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>
          
          <v-col
            cols="12"
            class="py-0"
          >
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="localBackupApprover.end_date"
                  v-validate="'required'"
                  :disabled="!localBackupApprover.start_date"
                  label="End Date"
                  :error-messages="errors.collect('end_date')"
                  data-vv-name="end_date"  
                  data-vv-as="end date"    
                  readonly
                  class="label-position"
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localBackupApprover.end_date"
                :disabled="!localBackupApprover.start_date"
                no-title
                :min="localBackupApprover.start_date > today ? localBackupApprover.start_date : today"
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <v-textarea
              v-model="localBackupApprover.reason"
              v-validate="'required|max:5000'"
              :rows="2"
              :error-messages="errors.collect('reason')"
              data-vv-name="reason"        
              label="Reason"
            />
          </v-col>
        </v-row>
        
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="progressing"
            text
            class="px-10"
            @click="dialog = false"
          >
            Close
          </v-btn>
          <v-btn
            :loading="progressing"
            color="hrissecondary"   
            class="white--text px-10"       
            @click="submitForm"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-container>
      <v-container
        v-else
        style="height: 150px;"
      >
        <v-row
          class="fill-height"
          align="center" 
          justify="center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </v-row>     
      </v-container>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      backupApprover: {
        type: Object,
        required: true,
      },
      employees: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,        
        loading: false,
        progressing: false,
        startDateMenu: false,
        startDate: null,
        endDateMenu: false,
        endDate: null,
        today: new Date().toISOString().slice(0, 10), 
        localBackupApprover: null,
        backups: [],
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localBackupApprover = { ...this.backupApprover }
        this.getBackup(this.localBackupApprover.delegator_uid)
      },
    },
    methods: {
      getBackup (delegator) {
        this.loading = true

        this.$api.backupApprovers().listBackup(delegator)
          .then((res) => {
            this.backups = _.orderBy(res.data, ['person.name'], ['asc'])
            this.loading = false
          })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true
    
            this.$api.backupApprovers().update(this.backupApprover.id, this.prepareFormData())
              .then((res) => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('updated')              
                  this.$snack({ message: 'Backup approver updated' })
                  this.$resetData()
                  this.dialog = false                 
                }
              })
              .catch(error => {
                if (error.response.data.message) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Failed to update new backup approver',
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        return {
          employee_uid: this.localBackupApprover.employee_uid,
          delegator_uid: this.localBackupApprover.delegator_uid,
          start_date: this.localBackupApprover.start_date,
          end_date: this.localBackupApprover.end_date,
          reason: this.localBackupApprover.reason,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
